import { DashboardWidgetTableColumn } from "../../models/dashboard-widget-table-column.model";

export const USER_PERFORMANCE_TARGET_CONFIG: Record<string, { label: string; description: string | null }> = {
    daily: {
        label: "Daily Interaction Target",
        description: null,
    },
    hourly: {
        label: "Hourly Interaction Target",
        description: null,
    },
    leads_generated: {
        label: "Leads Generated Target (per day)",
        description: "Based on interactions captured with your 1 positive interaction status",
    },
    shift_duration: {
        label: "Shift Duration Target",
        description: "In hours (calculated from time of first interaction and last interaction of the day)",
    },
    days_active: {
        label: "Days Active Target",
        description: "Number of days where the user has been active (based on user completing at least 1 interaction on a day)",
    },
};

export const USER_PERFORMANCE_LEVEL_CONFIG = {
    divisions: {
        next: "campaigns",
        label: "Divisions",
        columns: [
            "total_users",
            "active_users",
            "hourly_performance",
            "day_performance",
            "leads_generated_performance",
            "shift_duration_performance",
            "avg_canvasses_per_hour",
            "avg_canvasses_per_day",
            "avg_shift_hours",
            "avg_shift_start_time",
            "avg_shift_end_time",
            "days_active_performance",
        ],
    },
    campaigns: {
        next: "users",
        label: "Campaigns",
        columns: [
            "total_users",
            "active_users",
            "hourly_performance",
            "day_performance",
            "shift_duration_performance",
            "leads_generated_performance",
            "avg_canvasses_per_hour",
            "avg_canvasses_per_day",
            "avg_shift_hours",
            "avg_shift_start_time",
            "avg_shift_end_time",
            "days_active_performance",
        ],
    },
    users: {
        next: "days",
        label: "Users",
        columns: [
            "avg_canvasses_per_hour",
            "avg_canvasses_per_day",
            "leads_generated_per_day",
            "avg_shift_hours",
            "days_active",
            "avg_shift_start_time",
            "avg_shift_end_time",
        ],
    },
    days: {
        next: null,
        label: "Daily Breakdown",
        columns: [
            "avg_canvasses_per_hour",
            "avg_canvasses_per_day",
            "leads_generated_per_day",
            "avg_shift_hours",
            "avg_shift_start_time",
            "avg_shift_end_time",
        ],
    },
};

export const USER_PERFORMANCE_COLUMN_TYPES = {
    DEFAULT: { precision: 0, fallback: "0" },
    NUMBER: { precision: 0, fallback: "0" },
    DECIMAL: { precision: 1, fallback: "0" },
    NUMBER_AND_PERCENT: { precision: 0, fallback: "N/A" },
    STACKED_BAR: {},
    RATING: { precision: 1, fallback: "0.0", icon: "star" },
    PERCENT: { precision: 0, fallback: "N/A", suffix: "%" },
    DURATION: { filter: "hoursMinutesWords", fallback: "0" },
    TIME: { fallback: "N/A" },
};

export const USER_PERFORMANCE_COLUMN_DEFS = {
    total_users: USER_PERFORMANCE_COLUMN_TYPES.NUMBER,
    active_users: USER_PERFORMANCE_COLUMN_TYPES.NUMBER,
    hourly_performance: USER_PERFORMANCE_COLUMN_TYPES.STACKED_BAR,
    day_performance: USER_PERFORMANCE_COLUMN_TYPES.STACKED_BAR,
    leads_generated_per_day: USER_PERFORMANCE_COLUMN_TYPES.DECIMAL,
    shift_duration_performance: USER_PERFORMANCE_COLUMN_TYPES.STACKED_BAR,
    avg_canvasses_per_hour: USER_PERFORMANCE_COLUMN_TYPES.NUMBER,
    avg_canvasses_per_day: USER_PERFORMANCE_COLUMN_TYPES.NUMBER,
    avg_shift_hours: USER_PERFORMANCE_COLUMN_TYPES.DURATION,
    avg_shift_start_time: USER_PERFORMANCE_COLUMN_TYPES.TIME,
    avg_shift_end_time: USER_PERFORMANCE_COLUMN_TYPES.TIME,
    avg_canvasses_per_active_user: USER_PERFORMANCE_COLUMN_TYPES.NUMBER,
    avg_rating: USER_PERFORMANCE_COLUMN_TYPES.RATING,
    hours_worked: USER_PERFORMANCE_COLUMN_TYPES.DURATION,
    start_time: USER_PERFORMANCE_COLUMN_TYPES.TIME,
    end_time: USER_PERFORMANCE_COLUMN_TYPES.TIME,
    days_active: USER_PERFORMANCE_COLUMN_TYPES.NUMBER,
};

export interface UserPerformanceTableLevel {
    type: string;
    title: string;
    data: any[];
    parentId: number;
    activeColumns: any[];
}

function col(
    id: string,
    name: string,
    tooltip = "",
    selected = true
): DashboardWidgetTableColumn {
    return { id, name, tooltip, selected };
}

export type DashboardWidgetTableColumnKey =
    | "lists"
    | "users"
    | "user_performance"
    | "teams"
    | "streets";

export const DASHBOARD_DATA_TABLE_COLUMNS: Record<
    DashboardWidgetTableColumnKey,
    DashboardWidgetTableColumn[]
> = {
    lists: [
        col(
            "houses_visited",
            "Houses Visited",
            "Count of unique houses visited"
        ),
        col("avg_rating", "Average Rating"),
        col("conversion_rate", "Conversion Rate"),
    ],
    users: [
        col("avg_rating", "Average Rating"),
        col("conversion_rate", "Conversion Rate"),
        col(
            "days_worked",
            "Days active",
            "Number of days on which interactions were performed"
        ),
        col(
            "hours_worked",
            "Hours active",
            "Hours between first & last interaction of the day"
        ),
        col(
            "start_time",
            "Start Time",
            "Based on the first logged interaction in the day"
        ),
        col(
            "end_time",
            "End Time",
            "Based on the last logged interaction in the day"
        ),
        col("interactions", "Count of interactions"),
        col(
            "houses_visited",
            "Houses Visited",
            "Count of unique houses visited"
        ),
    ],
    user_performance: [
        col(
            "total_users",
            "Total Users",
            "Total number of users assigned to this division or campaign"
        ),
        col(
            "active_users",
            "Active Users",
            "Users who performed at least one interaction during the selected period"
        ),
        col(
            "hourly_performance",
            "Hourly Target Reached",
            "Percentage of active users who achieved hourly interaction target"
        ),
        col(
            "day_performance",
            "Daily Target Reached",
            "Percentage of active users who achieved daily interaction target"
        ),
        col(
            "leads_generated_performance",
            "Leads Generated Target Reached",
            "Percentage of active users who achieved daily leads generated target"
        ),
        col(
            "leads_generated_per_day",
            "Leads Generated Per Day",
            "Average of daily leads generated"
        ),
        col(
            "shift_duration_performance",
            "Shift Duration Target Reached",
            "Percentage of active users who achieved shift duration target"
        ),
        col(
            "days_active_performance",
            "Days Active Target Reached",
            "Percentage of active users who achieved their target for active days"
        ),
        col(
            "avg_canvasses_per_active_user",
            "Interactions per User",
            "Average count of interactions performed by active users"
        ),
        col(
            "avg_canvasses_per_hour",
            "Interactions per Hour",
            "Average number of interactions performed per hour worked"
        ),
        col(
            "avg_canvasses_per_day",
            "Interactions per Day",
            "Average number of interactions performed per day worked"
        ),
        col(
            "avg_shift_hours",
            "Shift Duration",
            "Average duration of shifts in hours"
        ),
        col(
            "avg_shift_start_time",
            "Start Time",
            "Average time of day when shift was started"
        ),
        col(
            "avg_shift_end_time",
            "End Time",
            "Average time of day when shift was ended"
        ),
        col(
            "days_active",
            "Days Active",
            "Number of days on which interactions were performed"
        ),
    ],
    teams: [
        col("interactions", "Count of interactions"),
        col("avg_rating", "Average Rating"),
        col("conversion_rate", "Conversion Rate"),
        col(
            "houses_visited",
            "Houses Visited",
            "Count of unique houses visited"
        ),
    ],
    streets: [
        col("interactions", "Count of interactions"),
        col("avg_rating", "Average Rating"),
        col("conversion_rate", "Conversion Rate"),
        col(
            "houses_visited",
            "Houses Visited",
            "Count of unique houses visited"
        ),
    ],
};
